import type { AbuseScenario, WatchdogProposal, FirstPass, SecondPass, ThirdPass, FinalDecision } from '../types';
import { democraticBandLabel } from '../utils/democraticBand';
import { isThirdPassV2, type AnyThirdPass, type ThirdPassV2 } from '../types/watchdog';
import { ThirdPassV2View } from './ThirdPassV2View';

interface Props {
  item: WatchdogProposal;
  onClose: () => void;
}

const RISK_LABEL: Record<string, string> = {
  low: 'Nízké',
  medium: 'Střední',
  high: 'Vysoké',
  critical: 'Kritické',
};

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <div className="confidence-bar" aria-label={`Spolehlivost ${pct} %`}>
      <div className="confidence-bar__fill" style={{ width: `${pct}%` }} />
      <span className="confidence-bar__label">{pct} %</span>
    </div>
  );
}

function StringList({ items, label }: { items: string[]; label: string }) {
  if (!items?.length) return null;
  return (
    <div className="detail-section">
      <h4 className="detail-section__title">{label}</h4>
      <ul className="detail-list">
        {items.map((s, i) => <li key={i}>{s}</li>)}
      </ul>
    </div>
  );
}

function formatAbuseScenario(scenario: string | AbuseScenario, index: number): { title: string; detail: string } {
  if (typeof scenario === 'string') {
    return { title: scenario, detail: '' };
  }
  return {
    title: scenario.scenario_title || `Scénář ${index + 1}`,
    detail: scenario.mechanism || scenario.trigger_conditions || scenario.textual_evidence || '',
  };
}

function AbuseScenarioList({ items, label }: { items: Array<string | AbuseScenario>; label: string }) {
  if (!items?.length) return null;
  return (
    <div className="detail-section">
      <h4 className="detail-section__title">{label}</h4>
      <ul className="detail-list">
        {items.map((rawScenario, i) => {
          const scenario = formatAbuseScenario(rawScenario, i);
          return (
            <li key={`${scenario.title}-${i}`}>
              <strong>{scenario.title}</strong>
              {scenario.detail && <p>{scenario.detail}</p>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PassBlock({ title, pass }: { title: string; pass: FirstPass | SecondPass | ThirdPass | null | undefined }) {
  if (!pass) return null;
  const riskLevel = (pass as FirstPass).risk_level ?? (pass as SecondPass).final_risk_level ?? '';
  const reasons = pass.reasons ?? [];
  const dmAreas = (pass as SecondPass).democratic_impact_areas ?? [];
  const keyMech = (pass as SecondPass).key_mechanisms ?? [];
  const watchFlags = (pass as FirstPass).watch_flags ?? [];
  const evidenceQuotes = (pass as FirstPass).evidence_quotes ?? [];

  return (
    <details className="pass-block">
      <summary className="pass-block__summary">
        {title}
        {riskLevel && (
          <span className={`risk-badge risk-badge--${riskLevel} pass-block__risk`}>
            {RISK_LABEL[riskLevel] ?? riskLevel}
          </span>
        )}
        {typeof pass.confidence === 'number' && (
          <span className="pass-block__confidence">{Math.round(pass.confidence * 100)} %</span>
        )}
      </summary>
      <div className="pass-block__body">
        {pass.plain_explanation && (
          <p className="detail-final-decision__explanation">{pass.plain_explanation}</p>
        )}
        {pass.summary && pass.summary !== pass.plain_explanation && (
          <div className="detail-section">
            <h4 className="detail-section__title">Shrnutí</h4>
            <p className="detail-section p">{pass.summary}</p>
          </div>
        )}
        <StringList items={reasons} label="Důvody" />
        <StringList items={watchFlags} label="Watch flags" />
        <StringList items={dmAreas} label="Dopad na demokratické oblasti" />
        <StringList items={keyMech} label="Klíčové mechanismy" />
        {evidenceQuotes.length > 0 && (
          <div className="detail-section">
            <h4 className="detail-section__title">Citace z návrhu</h4>
            {evidenceQuotes.map((q, i) => (
              <blockquote key={i} className="evidence-quote">
                <p className="evidence-quote__text">„{q.quote}"</p>
                <footer className="evidence-quote__footer">
                  <span className="evidence-quote__dimension">{q.dimension}</span>
                  {q.why_it_matters && (
                    <span className="evidence-quote__why"> — {q.why_it_matters}</span>
                  )}
                </footer>
              </blockquote>
            ))}
          </div>
        )}
      </div>
    </details>
  );
}

// V-20 additive erratum notice. A human review either degraded the published
// analysis (kept within the published range, no alert) or returned it to the
// curator. The notice is additive and never replaces the original record.
function CorrectionNotice({ item }: { item: WatchdogProposal }) {
  const correction = item.correction;
  if (!correction) return null;
  const isReturned = correction.state === 'returned_to_curator';
  if (!isReturned && correction.state !== 'degraded') return null;
  const label = isReturned
    ? 'Vráceno kurátorovi (mimo publikaci)'
    : 'Degradováno po lidské revizi (zůstává v rámci publikovaného rozsahu, bez alertu)';
  const date = correction.corrected_at
    ? new Date(correction.corrected_at).toLocaleString('cs-CZ')
    : '';
  return (
    <div className="watchdog-correction" role="note">
      <span className={`badge badge--correction watchdog-correction__title`}>
        {isReturned ? 'Vráceno kurátorovi' : 'Korekce (degradováno)'}
      </span>
      <p className="watchdog-correction__body">{label}</p>
      {date && <p className="watchdog-correction__date">Korekce: {date}</p>}
    </div>
  );
}

function FinalDecisionBlock({ fd }: { fd: FinalDecision }) {
  const isPlaceholder = fd.alert !== 'yes' && fd.alert !== 'no';
  return (
    <div className="detail-final-decision">
      <h3 className="detail-final-decision__heading">Finální rozhodnutí</h3>
      {isPlaceholder ? (
        <p className="badge badge--placeholder" style={{ display: 'inline-block' }}>Placeholder výstup</p>
      ) : (
        <>
          <div className="detail-panel__badges" style={{ marginBottom: '0.5rem' }}>
            {fd.alert === 'yes' && <span className="badge badge--alert">ALERT</span>}
            {fd.alert === 'no' && <span className="badge badge--ok">Bez alertu</span>}
          </div>
          {fd.plain_explanation && (
            <p className="detail-final-decision__explanation">{fd.plain_explanation}</p>
          )}
          {fd.summary && fd.summary !== fd.plain_explanation && (
            <div className="detail-section">
              <h4 className="detail-section__title">Souhrn</h4>
              <p>{fd.summary}</p>
            </div>
          )}
          <dl className="detail-meta">
            {fd.final_risk_level && (
              <><dt>Finální riziko</dt><dd>{RISK_LABEL[fd.final_risk_level] ?? fd.final_risk_level}</dd></>
            )}
            {fd.constitutional_relevance && (
              <><dt>Ústavní relevance</dt><dd>{RISK_LABEL[fd.constitutional_relevance] ?? fd.constitutional_relevance}</dd></>
            )}
            {typeof fd.final_score === 'number' && (
              <><dt>Skóre</dt><dd>{fd.final_score} <span className="democratic-band-chip">{democraticBandLabel(fd.final_score)}</span></dd></>
            )}
          </dl>
          {typeof fd.confidence === 'number' && (
            <div className="detail-section">
              <h4 className="detail-section__title">Spolehlivost</h4>
              <ConfidenceBar value={fd.confidence} />
            </div>
          )}
          <StringList items={fd.reasons} label="Důvody" />
          <StringList items={fd.democratic_impact_areas} label="Dopad na demokratické oblasti" />
          <StringList items={fd.key_mechanisms} label="Klíčové mechanismy" />
          <AbuseScenarioList items={fd.abuse_scenarios} label="Scenare a slaba mista k overeni" />
        </>
      )}
    </div>
  );
}

export function WatchdogDetail({ item, onClose }: Props) {
  const risk = item.final_decision?.final_risk_level
    ?? item.third_pass?.final_risk_level
    ?? item.second_pass?.final_risk_level
    ?? item.first_pass?.risk_level
    ?? '';

  return (
    <aside className="detail-panel" role="complementary" aria-label="Detail výsledku watchdog">
      <button className="detail-panel__close" onClick={onClose} aria-label="Zavřít detail">
        ✕
      </button>

      <div className="detail-panel__body">
        <div className="detail-panel__badges">
          {risk && (
            <span className={`risk-badge risk-badge--${risk}`}>
              {RISK_LABEL[risk] ?? risk}
            </span>
          )}
          {item.presentation_state && (
            <span className={`badge badge--${
              item.presentation_state === 'completed' ? 'ok' :
              item.presentation_state === 'placeholder' ? 'placeholder' :
              item.presentation_state === 'failed_retryable' || item.presentation_state === 'error' ? 'alert' :
              'incomplete'
            }`}>
              {item.presentation_state === 'completed' ? 'Dokončeno' :
               item.presentation_state === 'placeholder' ? 'Placeholder' :
               item.presentation_state === 'failed_retryable' ? 'Chyba (retry)' :
               item.presentation_state === 'no_pdf' ? 'PDF nedostupné' :
               item.presentation_state === 'error' ? 'Chyba' :
               'Rozpracováno'}
            </span>
          )}
        </div>

        <h2 className="detail-panel__title">{item.title}</h2>

        <CorrectionNotice item={item} />

        <dl className="detail-meta">
          {item.print_number && <><dt>Tisk č.</dt><dd>{item.print_number}</dd></>}
          {item.processing_stage && <><dt>Fáze</dt><dd>{item.processing_stage}</dd></>}
          {item.updated_at && (
            <><dt>Aktualizováno</dt><dd>{new Date(item.updated_at).toLocaleString('cs-CZ')}</dd></>
          )}
          {item.completed_at && (
            <><dt>Dokončeno</dt><dd>{new Date(item.completed_at).toLocaleString('cs-CZ')}</dd></>
          )}
        </dl>

        {item.last_error && (
          <div className="detail-section watchdog-error">
            <h4 className="detail-section__title">Chyba zpracování</h4>
            <p className="watchdog-error__msg">{item.last_error}</p>
          </div>
        )}

        {item.final_decision && <FinalDecisionBlock fd={item.final_decision} />}

        {(item.first_pass || item.second_pass || item.third_pass) && (
          <div className="passes-section">
            <h3 className="passes-section__heading">Průběh analýzy</h3>
            <PassBlock title="První průchod" pass={item.first_pass} />
            <PassBlock title="Druhý průchod" pass={item.second_pass} />
            {isThirdPassV2(item.third_pass as AnyThirdPass) ? (
              <ThirdPassV2View third={item.third_pass as unknown as ThirdPassV2} />
            ) : (
              <PassBlock title="Třetí průchod" pass={item.third_pass} />
            )}
          </div>
        )}

        {item.detail_url && (
          <div className="detail-panel__link">
            <a href={item.detail_url} target="_blank" rel="noopener noreferrer">
              Zobrazit na PSP.cz ↗
            </a>
          </div>
        )}
      </div>
    </aside>
  );
}
